import { BusinessHoursRange, BusinessHoursSchedule } from "@/model/interface/CallFlow";
import { UserPresence } from "@/model/interface/Membership";
import { WEEK_DAYS } from "@/util/callFlowNodes";

const MINUTE_MILLIS = 60_000;
const DAY_MILLIS = 86_400_000;
const NEARBY_DAYS = 6;
const TOMORROW_MORNING_HOUR = 8;
const DEFAULT_RANGE: BusinessHoursRange = { start: "09:00", end: "18:00" };

export const DEFAULT_WORKING_HOURS: BusinessHoursSchedule = {
  days: {
    MONDAY: [DEFAULT_RANGE],
    TUESDAY: [DEFAULT_RANGE],
    WEDNESDAY: [DEFAULT_RANGE],
    THURSDAY: [DEFAULT_RANGE],
    FRIDAY: [DEFAULT_RANGE],
  },
  holidays: [],
};

export interface AvailabilityDescription {
  isAvailable: boolean;
  label: string;
  detail: string;
}

export function hasWorkingHours(schedule?: BusinessHoursSchedule | null): boolean {
  return Object.values(schedule?.days ?? {}).some((ranges) => ranges.length > 0);
}

export function isSetUnavailable(presence?: UserPresence | null): boolean {
  if (!presence || presence.status === "ONLINE") {
    return false;
  }
  return !presence.unavailableUntil || new Date(presence.unavailableUntil).getTime() > Date.now();
}

export function describeAvailability(presence?: UserPresence | null): AvailabilityDescription {
  const changesAt = presence?.availabilityChangesAt;
  if (isSetUnavailable(presence)) {
    return {
      isAvailable: false,
      label: "Unavailable",
      detail: changesAt ? backLabel(changesAt) : "Until you turn it back on",
    };
  }
  if (presence?.availabilityMode === "WORKING_HOURS") {
    if (presence.availableNow) {
      return {
        isAvailable: true,
        label: "Available",
        detail: changesAt ? `Working hours, ${untilLabel(changesAt)}` : "Working hours",
      };
    }
    return {
      isAvailable: false,
      label: "Unavailable",
      detail: changesAt ? `Working hours, ${backText(changesAt)}` : "Outside working hours",
    };
  }
  return { isAvailable: true, label: "Available", detail: "Receiving calls" };
}

export function backLabel(iso: string): string {
  const text = backText(iso);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function backText(iso: string): string {
  const moment = new Date(iso);
  if (isToday(moment)) {
    return `back at ${timeOf(moment)}`;
  }
  return `back ${dayAndTimeOf(moment)}`;
}

export function untilLabel(iso: string): string {
  const moment = new Date(iso);
  if (isToday(moment)) {
    return `until ${timeOf(moment)}`;
  }
  return `until ${dayAndTimeOf(moment)}`;
}

export function minutesFromNow(minutes: number): string {
  return new Date(Date.now() + minutes * MINUTE_MILLIS).toISOString();
}

export function tomorrowMorning(): string {
  const moment = new Date();
  moment.setDate(moment.getDate() + 1);
  moment.setHours(TOMORROW_MORNING_HOUR, 0, 0, 0);
  return moment.toISOString();
}

export function timeOf(moment: Date): string {
  return moment.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

export function workingHoursSummary(schedule?: BusinessHoursSchedule | null): string {
  const openDays = WEEK_DAYS.filter((day) => (schedule?.days?.[day] ?? []).length > 0);
  if (openDays.length === 0) {
    return "No hours set";
  }
  const rangesOf = (day: string) => (schedule?.days?.[day] ?? []).map(rangeText).join(", ");
  const firstRanges = rangesOf(openDays[0]);
  const isSameEveryDay = openDays.every((day) => rangesOf(day) === firstRanges);
  const daysText = daySpanText(openDays);
  if (isSameEveryDay) {
    return `${daysText}, ${firstRanges}`;
  }
  return `${daysText}, hours vary`;
}

export function clockText(time: string): string {
  const [hourText, minuteText] = time.split(":");
  const hour = Number(hourText);
  const suffix = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  if (minuteText === "00") {
    return `${displayHour} ${suffix}`;
  }
  return `${displayHour}:${minuteText} ${suffix}`;
}

export function shortDayName(day: string): string {
  return day.charAt(0) + day.slice(1, 3).toLowerCase();
}

export function zonedNow(timeZone: string): { weekDay: string; minutes: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "long",
    hour: "numeric",
    minute: "numeric",
    hourCycle: "h23",
  }).formatToParts(new Date());
  const partValue = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? "";
  return {
    weekDay: partValue("weekday").toUpperCase(),
    minutes: Number(partValue("hour")) * 60 + Number(partValue("minute")),
  };
}

export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function rangeText(range: BusinessHoursRange): string {
  return `${clockText(range.start)} to ${clockText(range.end)}`;
}

function daySpanText(days: string[]): string {
  const dayIndexes = days.map((day) => WEEK_DAYS.findIndex((weekDay) => weekDay === day));
  const isConsecutive = dayIndexes.every(
    (dayIndex, position) => position === 0 || dayIndex === dayIndexes[position - 1] + 1,
  );
  if (isConsecutive && days.length > 2) {
    return `${shortDayName(days[0])} to ${shortDayName(days[days.length - 1])}`;
  }
  return days.map(shortDayName).join(", ");
}

function isToday(moment: Date): boolean {
  return moment.toDateString() === new Date().toDateString();
}

function dayAndTimeOf(moment: Date): string {
  if (moment.getTime() - Date.now() < NEARBY_DAYS * DAY_MILLIS) {
    return `${moment.toLocaleDateString("en-US", { weekday: "short" })} ${timeOf(moment)}`;
  }
  return `${moment.toLocaleDateString("en-US", { month: "short", day: "numeric" })}, ${timeOf(moment)}`;
}
